/** Settings → Notifications: where merchant alerts go, which events, low-stock thresholds. */
import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Input, InputNumber, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { fmtDate, Hint, toastErr, useSite } from './common';
import ShopVariantPicker from './ShopVariantPicker';

const { Text } = Typography;
const EVENTS = [['new_order', 'New order'], ['paid_order', 'Paid order'], ['return_requested', 'Return requested'], ['low_stock', 'Low stock'], ['oversold', 'Oversold'], ['payment_alert', 'Payment alert']];
const CHANNELS = [['email', 'Email'], ['telegram', 'Telegram'], ['slack', 'Slack'], ['discord', 'Discord']];
const STATUS = { sent: 'green', failed: 'red', pending: 'gold' };

const ShopNotifications = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [d, setD] = useState(null);
    const [f, setF] = useState({});
    const [saving, setSaving] = useState(false);
    const [picker, setPicker] = useState(false);

    const apply = (x) => {
        setD(x);
        setF({ notify_email: x.notify_email, low_stock_threshold: x.low_stock_threshold, chat_id: x.telegram.chat_id, bot_token: '', slack_url: '', discord_url: '', events: x.events });
    };
    useEffect(() => { api.get('/notifications').then(apply).catch(toastErr); }, [api]);
    if (!d) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;

    const set = (patch) => setF((o) => ({ ...o, ...patch }));
    const send = async (body, ok = t('Saved')) => {
        setSaving(true);
        try { apply(await api.put('/notifications', body)); message.success(ok); } catch (e) { toastErr(e); } finally { setSaving(false); }
    };
    const save = () => {
        const body = { notify_email: f.notify_email, low_stock_threshold: f.low_stock_threshold ?? null, events: f.events };
        if (f.bot_token || f.chat_id !== d.telegram.chat_id) body.telegram = { chat_id: f.chat_id, ...(f.bot_token ? { bot_token: f.bot_token } : {}) };
        if (f.slack_url) body.slack_url = f.slack_url;
        if (f.discord_url) body.discord_url = f.discord_url;
        send(body);
    };
    const test = async (channel) => {
        try { apply(await api.post('/notifications/test', { channel })); message.success(t('Test message queued')); } catch (e) { toastErr(e); }
    };
    const variant = async (id, low_stock_threshold) => {
        try { apply(await api.put(`/notifications/variants/${id}`, { low_stock_threshold })); } catch (e) { toastErr(e); }
    };
    const toggle = (ev, ch, on) => set({ events: { ...f.events, [ev]: { ...f.events[ev], [ch]: on } } });
    const actions = (ch, removeBody) => d[ch].configured && (
        <Space>
            <Tag color="green">{t('Connected')}</Tag>
            <Button size="small" onClick={() => test(ch)}>{t('Send test')}</Button>
            <Button size="small" danger type="text" onClick={() => send(removeBody, t('Removed'))}>{t('Remove')}</Button>
        </Space>
    );

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 820 }}>
            <Card size="small" title={t('Channels')}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                        <Text strong>{t('Email')}</Text>
                        <Input style={{ marginTop: 4 }} value={f.notify_email} placeholder={t('Your account email')} onChange={(e) => set({ notify_email: e.target.value })} />
                    </div>
                    <div>
                        <Space><Text strong>Telegram</Text><Hint title={t('Create a bot with @BotFather, add it to your chat, then enter its token and the chat ID.')} />{actions('telegram', { telegram: { bot_token: '', chat_id: '' } })}</Space>
                        <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                            <Input.Password autoComplete="new-password" value={f.bot_token} placeholder={d.telegram.configured ? t('Bot token (saved)') : t('Bot token')} onChange={(e) => set({ bot_token: e.target.value })} />
                            <Input value={f.chat_id} placeholder={t('Chat ID')} onChange={(e) => set({ chat_id: e.target.value })} />
                        </Space.Compact>
                    </div>
                    {[['slack', 'Slack', 'https://hooks.slack.com/services/…'], ['discord', 'Discord', 'https://discord.com/api/webhooks/…']].map(([ch, name, ph]) => (
                        <div key={ch}>
                            <Space><Text strong>{name}</Text>{actions(ch, { [`${ch}_url`]: '' })}</Space>
                            <Input.Password autoComplete="new-password" style={{ marginTop: 4 }} value={f[`${ch}_url`]} placeholder={d[ch].configured ? t('Webhook URL (saved)') : ph} onChange={(e) => set({ [`${ch}_url`]: e.target.value })} />
                        </div>
                    ))}
                </Space>
            </Card>

            <Card size="small" title={t('Events')}>
                <Table size="small" pagination={false} rowKey="k" scroll={{ x: 'max-content' }} dataSource={EVENTS.map(([k, label]) => ({ k, label }))}
                    columns={[{ title: t('Event'), dataIndex: 'label', render: (v) => t(v) },
                        ...CHANNELS.map(([ch, name]) => ({ title: t(name), key: ch, align: 'center', width: 90,
                            render: (_, r) => <Checkbox aria-label={`${t(r.label)} ${t(name)}`} checked={!!f.events[r.k]?.[ch]} disabled={ch !== 'email' && !d[ch].configured} onChange={(e) => toggle(r.k, ch, e.target.checked)} /> }))]} />
            </Card>

            <Card size="small" title={t('Low stock')}>
                <Space wrap>
                    <Text>{t('Alert at or below')}</Text>
                    <InputNumber min={0} max={1000000} precision={0} value={f.low_stock_threshold} placeholder={t('Off')} onChange={(v) => set({ low_stock_threshold: v })} />
                    <Button icon={<PlusOutlined />} onClick={() => setPicker(true)}>{t('Add a variant threshold')}</Button>
                </Space>
                {d.variant_overrides.length > 0 && (
                    <Table style={{ marginTop: 12 }} size="small" pagination={false} rowKey="variant_id" dataSource={d.variant_overrides}
                        columns={[{ title: t('Variant'), render: (_, r) => `${r.product_title}${r.variant_title ? ` — ${r.variant_title}` : ''}` },
                            { title: t('In stock'), dataIndex: 'inventory_quantity', width: 90 },
                            { title: t('Threshold'), width: 120, render: (_, r) => <InputNumber size="small" min={0} max={1000000} precision={0} defaultValue={r.low_stock_threshold} onBlur={(e) => { const v = Number(e.target.value); if (e.target.value !== '' && v !== r.low_stock_threshold) variant(r.variant_id, v); }} /> },
                            { title: '', width: 40, render: (_, r) => <Button size="small" type="text" danger aria-label={t('Remove')} icon={<DeleteOutlined />} onClick={() => variant(r.variant_id, null)} /> }]} />
                )}
            </Card>

            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>{t('Save')}</Button>

            {d.recent.length > 0 && (
                <Card size="small" title={t('Recent messages')} extra={<Button size="small" type="text" aria-label={t('Refresh')} icon={<ReloadOutlined />} onClick={() => api.get('/notifications').then((x) => setD(x)).catch(toastErr)} />}>
                    <Table size="small" pagination={false} rowKey="id" scroll={{ x: 'max-content' }} dataSource={d.recent}
                        columns={[{ title: t('Time'), dataIndex: 'created_at', render: (v) => fmtDate(v) }, { title: t('Channel'), dataIndex: 'channel' }, { title: t('Event'), dataIndex: 'event' },
                            { title: t('Status'), dataIndex: 'status', render: (v, r) => <Space><Tag color={STATUS[v]}>{v}</Tag>{r.error && <Text type="secondary">{r.error}</Text>}</Space> }]} />
                </Card>
            )}

            <ShopVariantPicker open={picker} onClose={() => setPicker(false)} onPick={(v) => { setPicker(false); variant(v.variant_id, f.low_stock_threshold ?? 5); }} />
        </Space>
    );
};

export default ShopNotifications;
